/**
 * Writing help on a block: the menu of what to do, and the card the answer
 * lands in.
 *
 * Nothing the model says reaches the proposal until somebody presses Replace
 * or Insert below, and both go through the canvas's history, so one undo takes
 * it back. The menu is not drawn at all when the system has no key — the
 * builder is complete without it. Decision 81.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import type { DraftSection, ProposalDraft } from './draft.js'
import { Prose } from './ProposalPage.js'

export type AssistAction = 'improve' | 'professional' | 'friendlier' | 'shorter' | 'longer' | 'fix' | 'write' | 'custom'

export interface AssistRequest {
  sectionKey: string
  action: AssistAction
  instruction: string | null
  /** Character offsets into the body; null acts on all of it. */
  selection: { start: number; end: number } | null
}

export interface Suggestion extends AssistRequest {
  status: 'loading' | 'done' | 'failed'
  text: string | null
  error: string | null
}

const ACTIONS: Array<{ action: Exclude<AssistAction, 'custom' | 'write'>; label: string; icon: IconName }> = [
  { action: 'improve', label: 'Improve the writing', icon: 'sparkles' },
  { action: 'professional', label: 'More professional', icon: 'shield' },
  { action: 'friendlier', label: 'Warmer and friendlier', icon: 'clients' },
  { action: 'shorter', label: 'Make it shorter', icon: 'shorter' },
  { action: 'longer', label: 'Make it longer', icon: 'longer' },
  { action: 'fix', label: 'Fix spelling and grammar', icon: 'check' },
]

/**
 * The provider's availability, and one function that asks it about a block.
 * A quote's context is read by the server from the saved proposal, so an
 * unsaved edit is flushed first.
 */
export function useAssist({
  target,
  draft,
  beforeAssist,
}: {
  target: { kind: 'quote'; quoteId: string } | { kind: 'template'; name: string }
  draft: ProposalDraft
  beforeAssist?: () => Promise<unknown>
}) {
  const trpc = useTRPC()
  const status = useQuery({ ...trpc.proposals.assistStatus.queryOptions(), staleTime: 5 * 60_000 })
  const quote = useMutation(trpc.proposals.assistQuote.mutationOptions())
  const template = useMutation(trpc.proposals.assistTemplate.mutationOptions())

  const ask = async (section: DraftSection, request: AssistRequest): Promise<string> => {
    const body = section.body
    const chosen = request.selection ? body.slice(request.selection.start, request.selection.end) : body
    const base = {
      action: request.action,
      instruction: request.instruction,
      text: chosen,
      surrounding: request.selection ? body : null,
      heading: section.title || null,
      blockKind: section.kind,
    }
    if (target.kind === 'quote') {
      await beforeAssist?.()
      return (await quote.mutateAsync({ ...base, quoteId: target.quoteId })).text
    }
    return (
      await template.mutateAsync({
        ...base,
        name: target.name || null,
        outline: draft.sections.map((entry) => ({ kind: entry.kind, title: entry.title, body: entry.body })),
        options: draft.options.map((option) => ({ name: option.name, summary: option.summary })),
      })
    ).text
  }

  return { available: status.data?.available === true, ask, errorOf: errorMessage }
}

export function AssistMenu({
  hasText,
  hasSelection,
  onPick,
  onClose,
}: {
  hasText: boolean
  hasSelection: boolean
  onPick: (action: AssistAction, instruction: string | null) => void
  onClose: () => void
}) {
  const [instruction, setInstruction] = useState('')
  const panel = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    input.current?.focus()
    const away = (event: PointerEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) onClose()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', escape, true)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', escape, true)
    }
  }, [onClose])

  return (
    <div
      ref={panel}
      role="menu"
      aria-label="Writing help"
      className="absolute top-full right-0 z-30 mt-1.5 w-72 rounded-xl bg-white p-1.5 font-sans shadow-xl ring-1 ring-ink-200"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <form
        className="flex items-center gap-1.5 rounded-lg bg-ink-50 px-2 ring-1 ring-ink-200 focus-within:ring-violet-400"
        onSubmit={(event) => {
          event.preventDefault()
          if (instruction.trim()) onPick('custom', instruction.trim())
        }}
      >
        <Icon name="sparkles" className="size-4 shrink-0 text-violet-600" />
        <input
          ref={input}
          aria-label="Ask for anything"
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          placeholder={hasText ? 'Ask for a change…' : 'Say what to write…'}
          className="h-9 min-w-0 flex-1 bg-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={!instruction.trim()}
          className="grid size-7 place-items-center rounded-md text-violet-700 hover:bg-violet-100 disabled:opacity-30"
        >
          <Icon name="send" className="size-3.5" />
        </button>
      </form>

      <p className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-ink-500 uppercase">
        {hasSelection ? 'The selected words' : hasText ? 'This block' : 'An empty block'}
      </p>
      {hasText ? (
        ACTIONS.map((item) => (
          <MenuItem key={item.action} icon={item.icon} onClick={() => onPick(item.action, null)}>
            {item.label}
          </MenuItem>
        ))
      ) : (
        <MenuItem icon="sparkles" onClick={() => onPick('write', null)}>
          Write it from the heading
        </MenuItem>
      )}
      {hasText && !hasSelection ? (
        <MenuItem icon="text" onClick={() => onPick('write', null)}>
          Write it again from scratch
        </MenuItem>
      ) : null}
    </div>
  )
}

function MenuItem({ icon, onClick, children }: { icon: IconName; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm text-ink-800 hover:bg-violet-50 hover:text-violet-900"
    >
      <Icon name={icon} className="size-4 text-ink-500" />
      {children}
    </button>
  )
}

export function SuggestionCard({
  suggestion,
  onReplace,
  onInsert,
  onRetry,
  onDiscard,
}: {
  suggestion: Suggestion
  onReplace: () => void
  onInsert: () => void
  onRetry: () => void
  onDiscard: () => void
}) {
  return (
    <div
      role="region"
      aria-label="Suggestion"
      aria-busy={suggestion.status === 'loading'}
      className="mt-3 overflow-hidden rounded-xl bg-gradient-to-br from-violet-50 to-white font-sans ring-1 ring-violet-200"
    >
      <div className="flex items-center gap-2 border-b border-violet-100 px-3 py-2 text-xs font-semibold text-violet-800">
        <Icon name="sparkles" className="size-3.5" />
        {suggestion.status === 'loading'
          ? 'Writing…'
          : suggestion.status === 'failed'
            ? 'That did not work'
            : suggestion.selection
              ? 'A suggestion for the selected words'
              : 'A suggestion'}
      </div>
      <div className="px-4 py-3 text-[15px] text-ink-800">
        {suggestion.status === 'loading' ? (
          <div className="flex flex-col gap-2" aria-hidden>
            <span className="h-3 w-11/12 animate-pulse rounded bg-violet-100" />
            <span className="h-3 w-4/5 animate-pulse rounded bg-violet-100" />
            <span className="h-3 w-2/3 animate-pulse rounded bg-violet-100" />
          </div>
        ) : suggestion.status === 'failed' ? (
          <p role="alert" className="text-sm text-red-700">
            {suggestion.error}
          </p>
        ) : (
          <Prose body={suggestion.text} className="font-proposal" />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-t border-violet-100 bg-white/60 px-3 py-2">
        {suggestion.status === 'done' ? (
          <>
            <CardButton primary onClick={onReplace}>
              {suggestion.selection ? 'Replace the selection' : 'Replace'}
            </CardButton>
            <CardButton onClick={onInsert}>Insert below</CardButton>
          </>
        ) : null}
        {suggestion.status !== 'loading' ? (
          <CardButton onClick={onRetry}>
            <Icon name="refresh" className="size-3.5" />
            Try again
          </CardButton>
        ) : null}
        <CardButton onClick={onDiscard}>Discard</CardButton>
      </div>
    </div>
  )
}

function CardButton({ primary, onClick, children }: { primary?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium',
        primary ? 'bg-violet-700 text-white hover:bg-violet-800' : 'text-ink-700 ring-1 ring-ink-200 hover:bg-ink-50',
      )}
    >
      {children}
    </button>
  )
}

/** What Replace and Insert below do to a body. */
export function applySuggestion(body: string, suggestion: Suggestion, how: 'replace' | 'insert'): string {
  const text = suggestion.text ?? ''
  const range = suggestion.selection && suggestion.selection.end <= body.length ? suggestion.selection : null
  if (how === 'replace') {
    return range ? `${body.slice(0, range.start)}${text}${body.slice(range.end)}` : text
  }
  const at = range ? range.end : body.length
  const before = body.slice(0, at).replace(/\s+$/, '')
  const after = body.slice(at).replace(/^\s+/, '')
  return [before, text, after].filter((part) => part !== '').join('\n\n')
}
